<template>
	<v-dialog
		v-model="localShowDialog"
		:width="$vuetify.display.mobile ? '95%' : '500px'"
		:persistent="savingDriveConfig"
		@click:outside="handleDismiss"
		@keydown.esc="handleDismiss"
	>
		<v-card class="sgnl-pb-0">
			<v-card-title class="dialog-header sgnl-subtitle-medium"
				>{{ dialogTitle }}
				<v-spacer />
				<!-- <TooltipButton
					color="accent"
					@click="openMountDataSourcesDialog()"
					width="64px"
					btnClass="sgnl-mx-8 sgnl-px-8 sgnl-text-center sgnl-paragraph-s-medium"
					tooltipText="New Data Source Mount Point"
				>
					<template #btnContent>
						<Add16 />
						<Download16 class="sgnl-ml-4" />
					</template>
				</TooltipButton> -->
				<v-btn
					icon
					variant="text"
					size="small"
					@click="handleDismiss"
				>
					<Close16 />
				</v-btn>
			</v-card-title>
			<v-divider />
			<v-card-text class="dialog-body">
				<v-form
					ref="driveConfigForm"
					v-model="formValid"
				>
					<div class="input-group">
						<div class="field-label">Data Drive Name (required)</div>
						<v-text-field
							v-model="driveConfig.Name"
							maxlength="100"
							:rules="[rules.required]"
							data-test="data-drive-name-input"
							@keydown.enter.prevent="saveDriveConfig"
							hide-details="auto"
						/>
					</div>
				</v-form>
				<v-divider />

				<div class="drive-sources">
					<div
						v-if="driveConfig.DataSources?.length"
						class="drive-source-list"
					>
						<DataSourceInlineCard
							v-for="dataSource in driveConfig.DataSources"
							:key="dataSource.ResourceID"
							:dataSourceID="dataSource.ResourceID"
							:dataSourceType="dataSource.ResourceType"
							:mountLocation="dataSource.Location"
							@mark-source-for-disconnect="addSourceToDisconnectList"
							@reset-source-disconnect-mark="removeSourceFromDisconnectList"
						/>
					</div>
					<v-btn
						variant="outlined"
						@click="openMountDataSourcesDialog"
					>
						<Add16 />
						<span>New Data Source Mount Point</span>
					</v-btn>
				</div>
				<v-alert
					v-if="alert.show"
					variant="outlined"
					:type="alert.type"
					border="start"
					class="sgnl-paragraph-s-regular"
				>
					{{ alert.text }}
				</v-alert>
			</v-card-text>
			<v-divider />
			<v-card-actions class="dialog-footer">
				<v-btn
					variant="outlined"
					@click="handleDismiss"
					>Cancel</v-btn
				>
				<v-btn
					variant="flat"
					color="primary"
					@click="saveDriveConfig"
					:loading="savingDriveConfig"
					:disabled="!formValid"
				>
					{{ modifyDriveConfigRequest ? "Save" : "Create Data Drive" }}
				</v-btn>
			</v-card-actions>
		</v-card>
		<MountDataSourcesDialog
			v-if="showMountDataSourcesDialog"
			:showDialog="showMountDataSourcesDialog"
			:initialMountConfig="mountConfigToUpdate"
			:dataSourceExcludeList="[...connectedDataSources, ...driveIDList]"
			@close-dialog="closeMountDataSourcesDialog"
			@set-data-sources="addDataSource"
			@reset-data-sources="closeMountDataSourcesDialog"
		/>
	</v-dialog>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Close16 from "@carbon/icons-vue/es/close/16";
import Add16 from "@carbon/icons-vue/es/add/16";
import { sdk } from "@/js/sdk";
import { SdkError, ERROR_CODES } from "@signaloid/cloud-compute-engine-client";

import MountDataSourcesDialog from "@/components/MountDataSourcesDialog.vue";
import DataSourceInlineCard from "@/components/DataSources/DataSourceInlineCard.vue";
import { monitoringCaptureError } from "@/plugins/monitoring";

import { cloneDeep } from "lodash";
import { mapState } from "pinia";
import { useDataSourcesStore } from "@/stores/dataSources";
import { DataDrive, LocalMountConfig } from "@/types/api/dataSources";

type ComponentData = {
	formValid: boolean;
	alert: Alert;
	modifyDriveConfigRequest: boolean;
	showMountDataSourcesDialog: boolean;
	savingDriveConfig: boolean;
	mountConfigToUpdate: undefined | LocalMountConfig;
	dataSourcesToRemove: any[];
	driveConfig: Partial<DataDrive>;
};

type Alert = {
	show: boolean;
	text: string;
	type: "error" | "warning" | "info" | "success";
};

export default defineComponent({
	name: "DriveConfigDialog",
	components: { Close16, Add16, MountDataSourcesDialog, DataSourceInlineCard },
	props: {
		showDialog: { type: Boolean, default: false },
		driveConfigToUpdate: { type: Object, default: undefined },
	},
	emits: {
		"update:showDialog": (_data: boolean) => true,
		"update-drives": () => true,
		"drive-config-updated": (_data: Partial<DataDrive>) => true,
		"drive-created": (_data: Partial<DataDrive>) => true,
	},
	data: (): ComponentData => ({
		formValid: false,
		alert: { show: false, text: "", type: "warning" },
		modifyDriveConfigRequest: false,
		showMountDataSourcesDialog: false,
		savingDriveConfig: false,
		mountConfigToUpdate: undefined,
		dataSourcesToRemove: [],
		driveConfig: { DriveID: undefined, Name: undefined, DataSources: [] },
	}),
	computed: {
		localShowDialog: {
			get() {
				return this.showDialog;
			},
			set(value) {
				this.$emit("update:showDialog", value);
			},
		},
		dialogTitle(): string {
			return this.modifyDriveConfigRequest ? "Modify Data Drive" : "Create a Data Drive";
		},
		rules() {
			return {
				required: (value) => !!value || "Required.",
			};
		},
		connectedDataSources(): string[] {
			if (!this.driveConfig.DataSources) {
				return [];
			}

			return this.driveConfig.DataSources.map((x) => {
				return x.ResourceID;
			});
		},
		...mapState(useDataSourcesStore, { driveIDList: "driveList" }),
	},
	methods: {
		handleDismiss() {
			if (this.savingDriveConfig) {
				return;
			}
			this.closeDialog();
		},
		closeDialog() {
			this.$emit("update:showDialog", false);
			this.alert = { show: false, text: "", type: "warning" };
		},
		async saveDriveConfig() {
			this.alert = { show: false, text: "", type: "warning" };
			//@ts-ignore FIXME:validate that no mount point is repeated
			this.$refs.driveConfigForm.validate();
			if (this.formValid) {
				this.savingDriveConfig = true;
				try {
					if (this.modifyDriveConfigRequest) {
						// HACK: workaround to keep the inline card from disappearing before the response succeeds
						if (!this.driveConfig.DriveID || !this.driveConfig) {
							throw new Error("incorrect Drive config not specified");
						}
						// @ts-ignore FIXME:
						const updatedDriveConfig = this.removeMarkedSourcesFromDriveConfig(cloneDeep(this.driveConfig));
						await sdk.drives.update(this.driveConfig.DriveID, updatedDriveConfig);
						this.$emit("update-drives");
						this.$emit("drive-config-updated", this.driveConfig);
						this.closeDialog();
					} else {
						if (!this.driveConfig.Name || !this.driveConfig.DataSources) {
							throw new Error("Invalid Drive name or data source config not specified");
						}
						await sdk.drives.create({
							Name: this.driveConfig.Name,
							DataSources: this.driveConfig.DataSources,
						});
						this.$posthog?.capture("drive_created", {
							data_source_count: this.driveConfig.DataSources?.length ?? 0,
						});
						this.$emit("update-drives");
						this.$emit("drive-created", this.driveConfig);
						this.closeDialog();
					}
				} catch (error) {
					monitoringCaptureError(error, "Save drive config");

					if (error instanceof SdkError) {
						this.alert = {
							show: true,
							text:
								error.code === ERROR_CODES.API_FORBIDDEN
									? `Error: You have exceeded the Data Drive limit for your tier.`
									: error.message || `Error: Could not create a Data Drive. Please try again.`,
							type: "error",
						};
					} else {
						this.alert = {
							show: true,
							text: `Error: Could not create a Data Drive. Please try again.`,
							type: "error",
						};
					}
				} finally {
					this.savingDriveConfig = false;
				}
			}
		},
		openMountDataSourcesDialog(initialMountConfig) {
			this.mountConfigToUpdate = initialMountConfig;
			this.showMountDataSourcesDialog = true;
		},
		addDataSource(dataSource: LocalMountConfig) {
			if (!this.driveConfig.DataSources) {
				this.driveConfig.DataSources = [];
			}
			this.driveConfig.DataSources.push({
				Object: "DataSource",
				ResourceID: dataSource.ResourceID,
				ResourceType: dataSource.ResourceType,
				Location: dataSource.Location,
			});

			this.closeMountDataSourcesDialog();
		},
		addSourceToDisconnectList(dataSourceID: string) {
			this.dataSourcesToRemove.push(dataSourceID);
		},
		removeSourceFromDisconnectList(dataSourceID: string) {
			this.dataSourcesToRemove.splice(
				this.dataSourcesToRemove.findIndex((x) => x === dataSourceID),
				1,
			);
		},
		removeMarkedSourcesFromDriveConfig(driveConfig: DataDrive) {
			if (this.dataSourcesToRemove) {
				this.dataSourcesToRemove.forEach((sourceIDToRemove) => {
					driveConfig.DataSources.splice(
						driveConfig.DataSources.findIndex((x) => x.ResourceID === sourceIDToRemove),
						1,
					);
				});
			}
			return driveConfig;
		},
		closeMountDataSourcesDialog() {
			this.showMountDataSourcesDialog = false;
		},
	},

	mounted() {
		if (this.driveConfigToUpdate) {
			this.modifyDriveConfigRequest = true;
			this.driveConfig = cloneDeep(this.driveConfigToUpdate);
		}
	},
});
</script>

<style scoped>
.drive-sources {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-8);
}

.drive-source-list {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-4);
}
</style>
